import { useComponentSelection } from "./useComponentSelection";
import type { ChatComponent } from "@rekonas/data-access";
import { Button } from "@rekonas/ui";

const colours = ["#9cb6ff", "#8a82ff", "#c77dff", "#63d6c8"];

export function SleepStageCard({ component, recording, onDraftError }: {
  component: ChatComponent; recording: string; onAcknowledge?: (component: ChatComponent) => void; onAccessFailure?: () => void; onDraftError?: (message: string) => void;
}) {
  const { selected: view, accepted, choose, dirty, conflict, error, keepDraft, discard, revision } = useComponentSelection({
    component, recording, value: c => c.state.representation || "pie", onDraftError,
    choice: representation => ({ representation }), selectedValue: choice => "representation" in choice ? choice.representation : "pie",
  });
  const p = component.payload;
  if (!p || p.type !== "sleep_stage_breakdown") return <aside className="sleep-card">This card version is unsupported. Read the assistant’s text answer above.</aside>;
  let offset = 0;
  const gradient = p.stages.map((s, i) => { const start = offset; offset += s.percent; return `${colours[i]} ${start}% ${offset}%`; }).join(", ");
  return <section className="sleep-card" aria-label="Sleep-stage breakdown">
    <h3>Sleep stages</h3>
    {p.status === "unavailable" ? <p>{p.reason}</p> : <>
      <p>Percentage of total sleep time · {p.total_sleep_minutes?.toLocaleString()} min</p>
      <div className="sleep-switch" role="group" aria-label="Chart representation">
        {(["pie", "bar"] as const).map(value => <button key={value} type="button" aria-pressed={view === value}
          onClick={() => choose(value)}>{value === "pie" ? "Pie" : "Bar"}</button>)}
      </div>
      <div className="sleep-visual" aria-hidden="true">
        {view === "pie" ? <div className="sleep-pie" style={{ background: `conic-gradient(${gradient})` }} /> :
          <div className="sleep-bars">{p.stages.map((s, i) => <div key={s.stage}><span>{s.stage}</span><div className="sleep-track"><div style={{ width: `${s.percent}%`, background: colours[i] }} /></div><span>{s.percent.toFixed(1)}%</span></div>)}</div>}
      </div>
      <table className="sleep-table"><caption>Sleep-stage values · {view === "pie" ? "Pie" : "Bar"} view</caption>
        <thead><tr><th scope="col">Stage</th><th scope="col">Sleep %</th><th scope="col">Minutes</th></tr></thead>
        <tbody>{p.stages.map((s, i) => <tr key={s.stage}><th scope="row"><span className="sleep-swatch" style={{ background: colours[i] }} />{s.stage}</th><td>{s.percent.toFixed(1)}%</td><td>{s.minutes.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td></tr>)}</tbody>
      </table>
      <small>Percentages are rounded for display; totals may differ by up to 0.2 percentage points.</small>
      <p role="status">{error || (dirty ? `Unsent ${view === "pie" ? "Pie" : "Bar"} selection · accepted ${accepted === "pie" ? "Pie" : "Bar"} · revision ${revision}` : `${view === "pie" ? "Pie" : "Bar"} accepted · revision ${revision}`)}</p>
      {conflict && <p role="alert">Accepted selection changed on another client. Review your draft before sending.</p>}
      {conflict && <Button variant="secondary" onClick={keepDraft}>Use this draft for next question</Button>}
      {dirty && <Button variant="secondary" onClick={discard}>Discard local draft</Button>}
    </>}
    <details><summary>Text alternative</summary><p>{p.fallback}</p></details>
  </section>;
}
